import React, { RefObject } from 'react';
import { useDispatch } from 'react-redux';

import analytics from '@ad-scripts/lib/analytics';
import { duplicateThread, renameThread } from './actions/threadActions';
import OverflowMenu, {
  OverflowMenuItem,
  OverflowMenuLink,
  usePopoverMenu,
} from 'src/presentation/components/pages/shared/OverflowMenu';
import { NewThreadModalRef } from './components/NewThreadModal';
import { SidekickThreadFast } from 'src/models/Sidekick/SidekickThreads';
import useOpenThread from './hooks/useOpenThread';

type SidekickThreadsActionsProps = {
  root: string;
  item: SidekickThreadFast;
  newThreadModalRef: RefObject<NewThreadModalRef>;
  className?: string;
  isEditorPage?: boolean;
  hasActions: boolean;
  handleExpertModeClick?: () => void;
  toggleEnabledHandler: (item: SidekickThreadFast) => void;
};

const SidekickThreadsActions = ({
  item,
  newThreadModalRef,
  root,
  className,
  isEditorPage,
  hasActions,
  handleExpertModeClick,
  toggleEnabledHandler,
}: SidekickThreadsActionsProps) => {
  const dispatch = useDispatch();

  const { onClose, ...popoverMenuProps } = usePopoverMenu();
  const { handleOpenThread } = useOpenThread({ root });

  const isEnabled = !!item.enabled;
  const activationMenuLabel = isEnabled ? 'Set as inactive' : 'Set as active';

  const enabledHandle = () => {
    analytics.track(`Admin - Sidekick Threads - ${activationMenuLabel}`, {
      threadId: item.id,
      threadName: item.name,
    });
    onClose();
    toggleEnabledHandler(item);
  };

  const handleRename = () => {
    newThreadModalRef.current?.show({
      confirmButtonLabel: 'Rename',
      subscribe: (newName: unknown) => {
        dispatch(renameThread({ threadId: Number(item.id), newName: newName as string }));
      },
      title: `Rename ${item.name}`,
      value: item.name,
      thread: item,
    });
  };

  const handleDuplicate = () => {
    newThreadModalRef.current?.show({
      confirmButtonLabel: 'Duplicate',
      subscribe: (newName: unknown) => {
        dispatch(duplicateThread({ threadId: item.id, threadName: newName as string }));
      },
      title: `Duplicate ${item.name}`,
      value: item.name,
      thread: item,
    });
  };

  // const hasActions = item.hasActions;

  return (
    <>
      <OverflowMenu className={className} id={item.id} onClose={onClose} {...popoverMenuProps}>
        {!isEditorPage && (
          <OverflowMenuItem id="editThread">
            <OverflowMenuLink onClick={() => handleOpenThread(item.id)} to="#">
              Edit
            </OverflowMenuLink>
          </OverflowMenuItem>
        )}
        <OverflowMenuItem id="renameThread">
          <OverflowMenuLink onClick={handleRename} to="#">
            Rename
          </OverflowMenuLink>
        </OverflowMenuItem>
        <OverflowMenuItem id="duplicateThread">
          <OverflowMenuLink onClick={handleDuplicate} to="#">
            Duplicate
          </OverflowMenuLink>
        </OverflowMenuItem>
        {isEditorPage && (
          <OverflowMenuItem id="expertMode">
            <OverflowMenuLink onClick={handleExpertModeClick} to="#">
              Expert Mode
            </OverflowMenuLink>
          </OverflowMenuItem>
        )}
        {hasActions && (
          <OverflowMenuItem id="activateThread">
            <OverflowMenuLink onClick={() => enabledHandle()} to="#">
              {activationMenuLabel}
            </OverflowMenuLink>
          </OverflowMenuItem>
        )}
      </OverflowMenu>
    </>
  );
};

export default SidekickThreadsActions;
